/**
 * @file AskPalette.tsx
 * @brief "Ask your meetings" in the Cmd+K palette's chrome.
 *
 * @details
 * DES (01-10-2026): "ask your meeting should look like cmd K". Same frame as
 * `CommandPalette`: a blurred backdrop, a panel that drops from under the
 * topbar across the page rail, white, a header band (icon, title, line, ✕),
 * the content, and a key-hint footer; the same open/close motion and
 * stagger. Escape or a click on the backdrop closes it. The content is the
 * caller's (`AskMeetingsPanel`, or a "no meetings yet" line).
 *
 * 01-10-2026: `icon`, `footer`, `closeLabel` and `autoFocus` (all optional,
 * Ask unchanged) so another page can borrow the frame (Profile trial).
 *
 * @author DES
 * @date 01-10-2026
 */

import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X, type LucideIcon } from "lucide-react";
import { AskSpark } from "@/components/icons/AskSpark";
import { GridBand, GridBlock } from "@/features/analytics/components/GridBlock";
import { shellOverlayMaxClass } from "@/layouts/components/shell-rail";
import { cn } from "@/shared/theme/cn";

const EASE = "cubic-bezier(0.16, 1, 0.3, 1)";
const CLOSE_MS = 240;

function staggerStyle(visible: boolean, delayMs: number): CSSProperties {
  return {
    transitionProperty: "opacity, transform",
    transitionDuration: "380ms",
    transitionTimingFunction: EASE,
    transitionDelay: visible ? `${delayMs}ms` : "0ms",
    opacity: visible ? 1 : 0,
    transform: visible ? "translateY(0)" : "translateY(10px)",
  };
}

export function AskPalette({
  open,
  onOpenChange,
  title = "Ask AI",
  description = "Ask anything about what was said, decided or agreed.",
  icon: Icon = AskSpark, // DES 08-10-2026: app icon, was Sparkles
  footer,
  closeLabel = "Close Ask AI",
  autoFocus = true,
  headerActions,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
  icon?: LucideIcon;
  /** Replaces the Ask key hints in the footer band. */
  footer?: ReactNode;
  closeLabel?: string;
  /** Focus the first field on open (the question box for Ask). */
  autoFocus?: boolean;
  /** Extra controls left of the ✕, e.g. Profile's account menu (05-10-2026). */
  headerActions?: ReactNode;
  children: ReactNode;
}) {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    if (open) {
      setMounted(true);
      const frame = requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
      return () => cancelAnimationFrame(frame);
    }
    setVisible(false);
    closeTimer.current = setTimeout(() => {
      setMounted(false);
      closeTimer.current = null;
    }, CLOSE_MS);
    return () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, [open]);

  useEffect(() => {
    if (!mounted) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [mounted, onOpenChange]);

  // Focus the first field (the question box) once it is in.
  useEffect(() => {
    if (!visible || !autoFocus) return;
    const id = window.setTimeout(
      () => panelRef.current?.querySelector<HTMLElement>("textarea, input")?.focus(),
      60,
    );
    return () => window.clearTimeout(id);
  }, [visible, autoFocus]);

  if (!mounted) return null;
  const close = () => onOpenChange(false);

  return createPortal(
    <>
      <button
        type="button"
        aria-label={closeLabel}
        tabIndex={visible ? 0 : -1}
        className={cn(
          "fixed inset-0 z-40 bg-black/45 backdrop-blur-[3px] transition-opacity duration-300 ease-out",
          visible ? "opacity-100" : "pointer-events-none opacity-0",
        )}
        onClick={close}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={cn(
          "fixed inset-x-0 top-14 z-50 max-h-[calc(100vh-3.5rem)] overflow-y-auto",
          !visible && "pointer-events-none",
        )}
        style={{
          transitionProperty: "opacity, transform",
          transitionDuration: visible ? "420ms" : `${CLOSE_MS}ms`,
          transitionTimingFunction: EASE,
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(-8px)",
        }}
      >
        <div className={shellOverlayMaxClass}>
          <GridBlock className="bg-background">
            <GridBand
              className="flex items-center justify-between gap-3 py-3"
              style={staggerStyle(visible, 40)}
            >
              <div className="flex min-w-0 items-center gap-2.5">
                <Icon className="h-4 w-4 shrink-0 text-primary" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold">{title}</p>
                  <p className="truncate text-xs text-muted-foreground">{description}</p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {headerActions}
                <button
                  type="button"
                  onClick={close}
                  className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-none text-muted-foreground transition-colors hover:bg-muted/40 hover:text-foreground"
                  aria-label={closeLabel}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </GridBand>

            <div className="border-b border-r border-border/60" style={staggerStyle(visible, 80)}>
              {children}
            </div>

            <GridBand
              className="flex items-center justify-between gap-3 py-2.5 text-[11px] text-muted-foreground"
              style={staggerStyle(visible, 120)}
            >
              {footer ?? (
                <>
                <span>
                  <kbd className="font-mono text-[10px]">↵</kbd> ask
                  <span className="mx-2 text-border">·</span>
                  <kbd className="font-mono text-[10px]">esc</kbd> close
                </span>
                <span className="hidden sm:inline">Answers cite the meeting they came from</span>
                </>
              )}
            </GridBand>
          </GridBlock>
        </div>
      </div>
    </>,
    document.body,
  );
}
